import { z } from 'zod';
import { DossiersFinancementSchema, FormattedDossiersFinancementSchema } from './dossiers-financement';
import { planFinancementFinanceurSchema, planFinancementSchema } from './plan-financement';
import { ComplementsSchema, DcpSchema, FormattedComplementsSchema, FormattedDcpSchema, FormattedDemandePaiementSchema, FormattedJustificationsSchema, JustificationsSchema, SharedSollicitationsSchema, } from './sollicitations';
import { ContributionVEFStatus } from './contributions-vef';
import { datetime } from './commons';
import { BourseVirtualStatus } from './bourse';
//====================================================
// Demande status enum
//====================================================
export var DemandesFinancementStatus;
(function (DemandesFinancementStatus) {
    DemandesFinancementStatus["requested"] = "REQUESTED";
    DemandesFinancementStatus["registered"] = "REGISTERED";
    DemandesFinancementStatus["transmitted"] = "TRANSMITTED";
    DemandesFinancementStatus["waitingForCertificate"] = "WAITING_FOR_CERTIFICATE";
    DemandesFinancementStatus["readyToJustify"] = "READY_TO_JUSTIFY";
    DemandesFinancementStatus["supported"] = "SUPPORTED";
    DemandesFinancementStatus["acceptable"] = "ACCEPTABLE";
    DemandesFinancementStatus["unacceptable"] = "UNACCEPTABLE";
    DemandesFinancementStatus["returned"] = "RETURNED";
    DemandesFinancementStatus["closed"] = "CLOSED";
})(DemandesFinancementStatus || (DemandesFinancementStatus = {}));
export const DemandesFinancementStatusEnum = z.nativeEnum(DemandesFinancementStatus);
export function isDemandeFinancementStatus(status) {
    return Object.values(DemandesFinancementStatus).includes(status);
}
//====================================================
// Demande virtual status enum
//====================================================
export const DemandesFinancementVirtualStatus = {
    requested: 'REQUESTED',
    registered: 'REGISTERED',
    transmitted: 'TRANSMITTED',
    waitingForCertificate: 'WAITING_FOR_CERTIFICATE',
    readyToJustify: 'READY_TO_JUSTIFY',
    supported: 'SUPPORTED',
    acceptable: 'ACCEPTABLE',
    unacceptable: 'UNACCEPTABLE',
    pretAVoter: 'PRETAVOTER',
    returned: 'RETURNED',
    finInstruction: 'FININSTRUCTION',
    cloture: 'CLOTURE',
};
export const DemandesFinancementVirtualStatusEnum = z.nativeEnum(DemandesFinancementVirtualStatus);
//====================================================
// Demande actions enum
//====================================================
export const DemandesFinancementActions = {
    RESUME: 'resume',
    ACCESS: 'access',
    DUPLICATE: 'duplicate',
    DELETE: 'delete',
    SHARE: 'share',
    MANAGE_RIGHTS: 'manage-rights',
    ADD_PIECES: 'add-pieces',
    DEPOT_DEMANDE_AIDE: 'openDemandeAideIframe',
    DEPOT_OFFRES_STAGE: 'openDemandeStageIframe',
    DEMANDES_LIST: 'openDemandesList',
    SHARED_DEMANDES_LIST: 'openSharedDemandesList',
    CREATE_PAYMENT: 'create-payment',
    ACCESS_PAYMENTS: 'access-payments',
    ACCESS_ALL_PAYMENTS: 'access-all-payments',
    CONTRIBUTION_INFORMATION: 'contribution-information',
};
export const DemandesFinancementActionsEnum = z.nativeEnum(DemandesFinancementActions);
//====================================================
// Demande tags enum
//====================================================
export const DemandesFinancementTags = {
    SHARED: 'shared',
    MANAGE_RIGHTS: 'restricted-access',
};
export const DemandesFinancementTagsEnum = z.nativeEnum(DemandesFinancementTags);
//====================================================
// Demande message type enum
//====================================================
export const DemandesFinancementMessageType = {
    AWAITING_ACTIONS: 'awaiting-actions',
    DOSSIERS_FINANCEMENT: 'dossiers-financement',
};
export const DemandesFinancementMessageTypeEnum = z.nativeEnum(DemandesFinancementMessageType);
//====================================================
// Demande pre instruction type enum
//====================================================
export const DemandesFinancementPreInstructionType = {
    PARTAGE: 'PARTAGE',
    SYNCHRONE: 'SYNCHRONE',
};
export const DemandesFinancementPreInstructionTypeEnum = z.nativeEnum(DemandesFinancementPreInstructionType);
//====================================================
// Actions
//====================================================
export const demandeFinancementActionSchema = z.object({
    id: z.string(),
    title: z.string(),
    variant: z.string().optional(),
    disabled: z.boolean().optional(),
    icon: z.object({ icon: z.string() }).optional(),
    href: z.string().optional(),
});
export const demandeActionsSchema = z.array(demandeFinancementActionSchema);
//====================================================
// Tags
//====================================================
export const demandeFinancementTagSchema = z.object({
    id: z.string(),
    label: z.string(),
    icon: z.object({ icon: z.string().optional() }).optional(),
    badge: z.object({ value: z.number(), label: z.string() }).optional(),
});
export const demandeTagsSchema = z.array(demandeFinancementTagSchema);
//====================================================
// Virtual Histroy Status
//====================================================
const VirtualHistoryStatusSchema = z.union([
    z.nativeEnum(BourseVirtualStatus),
    z.nativeEnum(DemandesFinancementStatus),
    z.literal('CONTRIBUTION_INFORMATION'),
]);
//====================================================
// Virtual Histroy Detail
//====================================================
export const VirtualHistoryDetailSchema = z.object({
    status: VirtualHistoryStatusSchema.optional(),
    customStatusTitle: z.string().optional(),
    label: z.string(),
});
//====================================================
// Front formatted demande
//====================================================
export const FormattedDemandeFinancementSchema = z.object({
    id: z.string(),
    title: z.string(),
    historydetails: z.array(z.string()),
    virtualHistoryDetails: z.array(VirtualHistoryDetailSchema).optional(),
    history: z.object({
        begin: z
            .object({
            date: datetime,
            user: z.object({
                href: z.string(),
            }),
        })
            .optional(),
        events: z.array(z.object({ date: datetime, reference: z.string() })).optional(),
    }),
    status: z.string().optional(),
    subActionsBtnLabel: z.string(),
    reference: z.string(),
    teleservice: z.object({
        href: z.string(),
        reference: z.string(),
        title: z.string(),
        validationEntreeFormation: z.boolean(),
    }),
    dossiersFinancement: FormattedDossiersFinancementSchema.optional(),
    messages: z
        .array(z.object({
        label: z.string().optional(),
        link: z.string().optional(),
        toggleOpened: z.string().optional(),
        toggleClosed: z.string().optional(),
        variant: z.string().optional(),
        type: DemandesFinancementMessageTypeEnum,
        displayDetails: z.boolean().optional(),
        expanded: z.boolean().optional(),
    }))
        .optional(),
    statusKey: DemandesFinancementStatusEnum,
    tags: demandeTagsSchema,
    demandeur: z.object({
        href: z.string(),
        title: z.string(),
        reference: z.string().optional(),
    }),
    signataire: z.object({
        href: z.string(),
        title: z.string(),
        email: z.string().optional(),
    }),
    isRestricted: z.boolean(),
    awaitingActions: z
        .array(FormattedComplementsSchema.or(FormattedDcpSchema)
        .or(FormattedJustificationsSchema)
        .or(FormattedDemandePaiementSchema))
        .optional(),
    actions: demandeActionsSchema,
    subactions: demandeActionsSchema,
    multiFinanceur: z.boolean().optional(),
    planFinancement: z.array(planFinancementSchema).optional(),
    beneficiaires: z.array(z.object({})).optional(),
    multiFinancement: z
        .object({
        financeurs: z.array(planFinancementFinanceurSchema).optional(),
    })
        .optional(),
    financeurPrincipal: z.object({ href: z.string() }).optional(),
    cloture: z
        .object({
        value: z.boolean(),
    })
        .optional(),
});
//====================================================
// VirtualHistoryEvent
//====================================================
export const VirtualHistoryEventSchema = z.object({
    date: datetime,
    label: z.string().optional(),
    status: VirtualHistoryStatusSchema,
    userFullName: z.string().optional(),
});
//====================================================
// Api returned demande
//====================================================
export const DemandeFinancementSchema = z.object({
    id: z.string(),
    reference: z.string(),
    title: z.string(),
    libelle: z
        .object({
        value: z.string(),
        lang: z.string(),
    })
        .optional(),
    referenceAdministrative: z.string(),
    status: DemandesFinancementStatusEnum,
    virtualStatus: z.string(),
    history: z.object({
        begin: z
            .object({
            date: datetime,
            user: z.object({
                href: z.string(),
            }),
        })
            .optional(),
        events: z.array(z.object({ date: datetime, reference: z.string(), type: z.string().optional() })).optional(),
    }),
    dossiersFinancement: DossiersFinancementSchema.optional(),
    demandeur: z.object({
        href: z.string(),
        title: z.string(),
        reference: z.string().optional(),
    }),
    demandesComplementPieces: z.array(z.object({})),
    hasContributionModification: z.boolean(),
    justification: z
        .object({
        history: z
            .object({
            events: z.array(z.object({ date: datetime, reference: z.string() })).optional(),
        })
            .optional(),
        statut: z.string().optional(),
    })
        .optional(),
    signataire: z.object({
        href: z.string(),
        title: z.string(),
        email: z.string().optional(),
    }),
    teleservicePaiement: z.string(),
    shareCount: z.number(),
    primaryActions: z.array(z.object({
        type: z.string(),
        enable: z.boolean().optional(),
    })),
    secondaryActions: z.array(z.object({ type: z.string(), value: z.string().optional() })),
    awaitingActions: z
        .array(z.union([ComplementsSchema, DcpSchema, JustificationsSchema, SharedSollicitationsSchema]))
        .optional(),
    teleservice: z.object({
        href: z.string(),
        reference: z.string(),
        title: z.string(),
        validationEntreeFormation: z.boolean(),
    }),
    idCpo: z.string().optional(),
    exerciceBudgetaire: z.number().optional(),
    multiFinanceur: z.boolean().optional(),
    planFinancement: z.array(planFinancementSchema).optional(),
    beneficiaires: z.array(z.object({})).optional(),
    cloture: z
        .object({
        value: z.boolean(),
        date: datetime.optional(),
    })
        .optional(),
    avisPrealables: z.array(z.object({})).optional(),
    multiFinancement: z
        .object({
        financeurs: z.array(planFinancementFinanceurSchema).optional(),
    })
        .optional(),
    financeurPrincipal: z.object({ href: z.string() }).optional(),
    optionsMultiFinanceur: z
        .object({
        modePreInstruction: z.string().optional(),
    })
        .optional(),
    virtualHistory: z.array(VirtualHistoryEventSchema).optional(),
    bourse: z
        .object({
        formationDateEntree: z.string().optional(),
    })
        .optional(),
    contributionVef: z
        .object({
        status: z.nativeEnum(ContributionVEFStatus),
    })
        .optional(),
});
//====================================================
// Aggregations
//====================================================
export const AggregationsSchema = z.object({
    libelleTeleservice: z.object({}),
    exerciceBudgetaire: z.object({}),
    status: z.object({}),
});
//====================================================
// Api returned payload
//====================================================
export const DemandesFinancementSchema = z.object({
    meta: z.object({ total: z.number(), limit: z.number(), offset: z.number() }),
    data: z.array(DemandeFinancementSchema),
    links: z.object({ self: z.string(), first: z.string(), last: z.string() }),
    authorized: z.array(z.string()).optional(),
    aggregations: AggregationsSchema,
});
//====================================================
// Filter demande payload
//====================================================
export const FiltersDemandesFinancementSchema = z.object({
    text: z.string(),
    status: z.array(z.string()),
    teleservice: z.array(z.string()),
    operatorExercice: z.string(),
    dateExercice: z.string(),
    mapAvis: z.object({}).optional(),
});
//====================================================
// Demande Entity
//====================================================
export const DemandeFinancementEntitySchema = z.object({
    id: z.string(),
    title: z.string(),
    reference: z.string(),
});
//# sourceMappingURL=demandes-financement.js.map